import {
  type Chain,
  type DynamicClient,
  type WalletProviderMetadata,
  assertWalletAccountSigningAvailability,
  getWalletAccountAddressByType,
} from '@dynamic-labs-sdk/client';
import {
  DYNAMIC_ICONIC_SPRITE_URL,
  assertDefined,
  createWalletProviderEventEmitter,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
} from '@dynamic-labs-sdk/client/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import { Psbt } from 'bitcoinjs-lib';
import type {
  AccountChangeEvent,
  BitcoinProvider,
  InputToSign,
  SignMultiplePsbtPayload,
} from 'sats-connect';
import {
  RpcErrorCode,
  addListener,
  request as satsConnectRequest,
} from 'sats-connect';

import type {
  BitcoinSendTransactionResponse,
  BitcoinWalletProvider,
} from '../../../../BitcoinWalletProvider.types';
import { InvalidPsbtError } from '../../../../errors/InvalidPsbtError';
import { bitcoinExecuteCheckoutTransaction } from '../../../../utils/bitcoinExecuteCheckoutTransaction';
import { bitcoinTransferAmount } from '../../../../utils/bitcoinTransferAmount';
import { convertNetworkIdForPsbt } from '../../../../utils/convertNetworkIdForPsbt';
import { convertNetworkIdToSatsConnectNetworkType } from '../../../../utils/convertNetworkIdToSatsConnectNetworkType';
import { getNetworkIdFromNetworkName } from '../../../../utils/getNetworkIdFromNetworkName';
import { getPsbtInputsToSignForSatsConnect } from '../../../../utils/getPsbtInputsToSignForSatsConnect';
import { getSatsConnectSigningProtocol } from '../../../../utils/getSatsConnectSigningProtocol';
import { parseBitcoinConnectionResult } from '../../../../utils/parseBitcoinConnectionResult';
import { signMultipleTransactionsWithSatsConnect } from '../../../../utils/signMultipleTransactionsWithSatsConnect';

export const XVERSE_METADATA: WalletProviderMetadata = {
  displayName: 'Xverse',
  icon: `${DYNAMIC_ICONIC_SPRITE_URL}#xverse`,
  supportedHardwareWalletVendors: ['ledger'],
};

type CreateXverseBitcoinWalletProviderParams = {
  dynamicClient: DynamicClient;
  satsConnectProvider: BitcoinProvider;
};

// https://docs.xverse.app/sats-connect/wallet-methods/request-methods
export const createXverseBitcoinWalletProvider = ({
  dynamicClient,
  satsConnectProvider,
}: CreateXverseBitcoinWalletProviderParams): BitcoinWalletProvider => {
  const chain: Chain = 'BTC';

  const walletProviderType = WalletProviderEnum.BrowserExtension;

  const key = formatWalletProviderKey({
    chain,
    displayName: XVERSE_METADATA.displayName,
    walletProviderType,
  });

  const requestPermissions = async (): Promise<void> => {
    const response = await satsConnectRequest(
      'wallet_requestPermissions',
      undefined
    );

    if (response.status === 'success') {
      return;
    }

    throw response.error;
  };

  const connect: BitcoinWalletProvider['connect'] = async () => {
    // requesting permissions on initial connect will avoid
    // connection prompt for other requests
    await requestPermissions();

    const response = await satsConnectRequest('wallet_connect', null);

    if (response.status !== 'success') {
      throw response.error;
    }

    const { addresses: connectedAddresses } = response.result;

    const { addresses } = parseBitcoinConnectionResult({
      connectedAddresses,
    });

    return {
      addresses,
    };
  };

  const disconnect: BitcoinWalletProvider['disconnect'] = async () => {
    await satsConnectRequest('wallet_renouncePermissions', null);
  };

  const getActiveNetworkId: BitcoinWalletProvider['getActiveNetworkId'] =
    async () => {
      const response = await satsConnectRequest('wallet_getNetwork', null);

      let networkName = 'mainnet';
      if (response.status === 'success') {
        networkName = response.result.bitcoin.name.toLowerCase();
      }

      return getNetworkIdFromNetworkName({ networkName }, dynamicClient);
    };

  const getConnectedAddresses: BitcoinWalletProvider['getConnectedAddresses'] =
    async () => {
      const response = await satsConnectRequest('wallet_getAccount', null);

      if (response.status === 'success') {
        return {
          addresses: response.result.addresses.map(
            (address) => address.address
          ),
        };
      }

      if (response.error.code !== RpcErrorCode.ACCESS_DENIED) {
        throw response.error;
      }

      // if error is access denied, it means that the selected account is not
      // connected to the app, so return empty array
      return {
        addresses: [],
      };
    };

  const sendBitcoin: BitcoinWalletProvider['sendBitcoin'] = async ({
    transaction,
    walletAccount,
  }): Promise<BitcoinSendTransactionResponse> => {
    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const response = await satsConnectRequest('sendTransfer', {
      recipients: [
        {
          address: transaction.recipientAddress,
          amount: Number(transaction.amount),
        },
      ],
    });

    if (response.status !== 'success') {
      throw response.error;
    }

    return {
      transactionId: response.result.txid,
    };
  };

  const signMessage: BitcoinWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
    protocol,
    addressType,
  }) => {
    assertDefined(walletAccount, 'Wallet account not found');

    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    let addressToSignWith = walletAccount.address;
    if (addressType) {
      addressToSignWith =
        getWalletAccountAddressByType({
          type: addressType,
          walletAccount,
        }) ?? addressToSignWith;
    }

    const response = await satsConnectRequest('signMessage', {
      address: addressToSignWith,
      message,
      protocol: getSatsConnectSigningProtocol(protocol),
    });

    if (response.status !== 'success') {
      throw response.error;
    }

    return {
      signature: response.result.signature,
    };
  };

  const signPsbt: BitcoinWalletProvider['signPsbt'] = async ({
    request,
    walletAccount,
  }) => {
    if (!request.allowedSighash.length) {
      throw new InvalidPsbtError('allowedSighash cannot be an empty array');
    }

    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const activeNetworkId = await getActiveNetworkId();
    const network = convertNetworkIdForPsbt(activeNetworkId.networkId);

    const psbtFromBase64 = Psbt.fromBase64(request.unsignedPsbtBase64, {
      network,
    });

    const inputsToSign = getPsbtInputsToSignForSatsConnect({
      psbt: psbtFromBase64,
      request,
    });

    const signPbstPayload = {
      broadcast: false,
      psbt: request.unsignedPsbtBase64,
      // we have to combine signing indexes for like addresses
      signInputs: inputsToSign.reduce(
        (accum: { [address: string]: number[] }, curr: InputToSign) => {
          if (!accum[curr.address]) {
            accum[curr.address] = [];
          }
          accum[curr.address].push(...curr.signingIndexes);
          return accum;
        },
        {}
      ),
    };

    const response = await satsConnectRequest('signPsbt', signPbstPayload);

    if (response.status !== 'success') {
      throw response.error;
    }

    return { signedPsbt: response.result.psbt };
  };

  const signPsbts: BitcoinWalletProvider['signPsbts'] = async ({
    requests,
    walletAccount,
  }) => {
    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const activeNetworkId = await getActiveNetworkId();
    const network = convertNetworkIdForPsbt(activeNetworkId.networkId);
    const networkType = convertNetworkIdToSatsConnectNetworkType(
      activeNetworkId.networkId
    );

    const psbtsToSign: SignMultiplePsbtPayload[] = [];

    for (const request of requests) {
      const psbtFromBase64 = Psbt.fromBase64(request.unsignedPsbtBase64, {
        network,
      });
      const inputsToSign = getPsbtInputsToSignForSatsConnect({
        psbt: psbtFromBase64,
        request,
      });

      psbtsToSign.push({
        inputsToSign,
        psbtBase64: request.unsignedPsbtBase64,
      });
    }

    const response = await signMultipleTransactionsWithSatsConnect({
      network: {
        type: networkType,
      },
      provider: satsConnectProvider,
      psbts: psbtsToSign,
    });

    return {
      signedPsbts: response.signedPsbts,
    };
  };

  const { getEventEmitter, cleanupEventEmitter } =
    createWalletProviderEventEmitter<{
      accountDisconnectedListener: VoidFunction;
      removeAccountChangeListener: VoidFunction;
    }>({
      removeEventListeners: ({ setupReturnValue }) => {
        assertDefined(setupReturnValue, 'Setup return value not defined');

        setupReturnValue.removeAccountChangeListener();
        setupReturnValue.accountDisconnectedListener();
      },

      setupEventListeners: ({ handleAccountsChanged, handleDisconnected }) => {
        const removeAccountChangeListener = addListener(
          'accountChange',
          ({ addresses }: AccountChangeEvent) => {
            if (!addresses) {
              return;
            }

            handleAccountsChanged({
              addresses: addresses.map((address) => address.address),
            });
          }
        );

        const accountDisconnectedListener = addListener(
          'accountDisconnected',
          () => {
            handleDisconnected();
          }
        );

        return {
          accountDisconnectedListener,
          removeAccountChangeListener,
        };
      },

      supportedEvents: ['accountsChanged', 'disconnected'],
    });

  const terminate: BitcoinWalletProvider['terminate'] = async () => {
    cleanupEventEmitter();
  };

  return {
    chain,
    connect,
    disconnect,
    get events() {
      return getEventEmitter();
    },
    executeCheckoutTransaction: (args) =>
      bitcoinExecuteCheckoutTransaction(args, dynamicClient),
    getActiveNetworkId,
    getConnectedAddresses,
    groupKey: formatWalletProviderGroupKey(XVERSE_METADATA.displayName),
    key,
    metadata: XVERSE_METADATA,
    sendBitcoin,
    signMessage,
    signPsbt,
    signPsbts,
    terminate,
    transferAmount: (args) => bitcoinTransferAmount(args, dynamicClient),
    walletProviderType,
  };
};
